import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginAPI, registerAPI, getProfileAPI } from '../services/auth';
import { getHistoryAPI, deleteConversationAPI } from '../services/history';

const CONVERSATIONS_STORAGE_KEY = 'conversationsState';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const createConversationId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };
  const createEmptyConversation = (title = 'New Chat') => ({ title, messages: [] });
  const getDefaultConversations = () => {
    const id = createConversationId();
    return { [id]: createEmptyConversation() };
  };
  const getNavigationType = () => {
    if (typeof window === 'undefined' || !window.performance) return null;
    const entries = window.performance.getEntriesByType?.('navigation');
    if (entries && entries.length > 0) {
      return entries[0].type;
    }
    if (window.performance.navigation) {
      return window.performance.navigation.type === 1 ? 'reload' : 'navigate';
    }
    return null;
  };
  const shouldRestoreSession = () => {
    const navType = getNavigationType();
    return navType === 'reload' || navType === 'back_forward';
  };
  const loadStoredConversations = () => {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      if (!shouldRestoreSession()) {
        const conversations = getDefaultConversations();
        const activeConversationId = Object.keys(conversations)[0];
        return { conversations, activeConversationId, loadedFromStorage: false };
      }
      const stored = sessionStorage.getItem(CONVERSATIONS_STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const storedConversations = parsed?.conversations;
          if (storedConversations && typeof storedConversations === 'object') {
            const ids = Object.keys(storedConversations);
            if (ids.length > 0) {
              const storedActiveId = parsed?.activeConversationId;
              const activeConversationId =
                storedActiveId && storedConversations[storedActiveId] ? storedActiveId : ids[0];
              return { conversations: storedConversations, activeConversationId, loadedFromStorage: true };
            }
          }
        } catch (error) {
          console.warn('Failed to parse stored conversations:', error);
        }
      }
    }
    const conversations = getDefaultConversations();
    const activeConversationId = Object.keys(conversations)[0];
    return { conversations, activeConversationId, loadedFromStorage: false };
  };

  const storedState = loadStoredConversations();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('authToken'));
  const [appLoading, setAppLoading] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const openAddUserModal = () => setIsAddUserModalOpen(true);
  const closeAddUserModal = () => setIsAddUserModalOpen(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentPreviewTarget, setDocumentPreviewTarget] = useState(null);
  const openDocumentModal = () => setIsDocumentModalOpen(true);
  const closeDocumentModal = () => setIsDocumentModalOpen(false);
  const openDocumentModalWith = (filename) => {
    setDocumentPreviewTarget(filename || null);
    setIsDocumentModalOpen(true);
  };
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => setIsUploadModalOpen(false);
  const [theme, setTheme] = useState('light');
  const [conversations, setConversations] = useState(storedState.conversations);
  const [activeConversationId, setActiveConversationId] = useState(storedState.activeConversationId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputDraft, setInputDraft] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async (authToken) => {
    if (!authToken) return;

    const buildConversationFromEntry = (entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const conversationId = entry.conversation_id || entry.id;
      if (!conversationId) return null;
      const msgs = Array.isArray(entry.messages)
        ? entry.messages
            .map((msg) => {
              if (!msg || typeof msg !== 'object') return null;
              const role = msg.role === 'assistant' ? 'bot' : msg.role;
              if (!role || !msg.content) return null;
              const formatted = { role, content: msg.content };
              if (msg.sources) formatted.sources = msg.sources;
              return formatted;
            })
            .filter(Boolean)
        : [];
      if (msgs.length === 0) {
        if (entry.question) {
          msgs.push({ role: 'user', content: entry.question });
        }
        if (entry.answer) {
          const botMessage = { role: 'bot', content: entry.answer };
          if (entry.sources) botMessage.sources = entry.sources;
          msgs.push(botMessage);
        }
      }
      if (msgs.length === 0) return null;
      const firstUser = msgs.find((msg) => msg.role === 'user');
      const titleText = entry.title || firstUser?.content || `Conversation ${index + 1}`;
      return {
        id: conversationId,
        title: titleText.substring(0, 50) || `Conversation ${index + 1}`,
        messages: msgs,
      };
    };

    try {
      const historyEntries = await getHistoryAPI(authToken);
      const conversationsFromHistory = {};
      historyEntries.forEach((entry, idx) => {
        const conv = buildConversationFromEntry(entry, idx);
        if (conv) {
          conversationsFromHistory[conv.id] = { title: conv.title, messages: conv.messages };
        }
      });
      if (Object.keys(conversationsFromHistory).length === 0) return;
      setConversations(prev => {
        const merged = { ...prev };
        Object.entries(conversationsFromHistory).forEach(([id, convo]) => {
          if (!merged[id]) {
            merged[id] = convo;
          }
        });
        return merged;
      });
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, []);

  // Validate token and fetch profile on load
  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const profile = await getProfileAPI(token);
          setUser(profile);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('authToken');
          setToken(null);
          setHistoryLoaded(false);
        }
      }
      setAppLoading(false);
    };
    validateToken();
  }, [token]);

  // Load history once after auth, without overriding current conversation
  useEffect(() => {
    if (!token || historyLoaded) return;
    let cancelled = false;
    const run = async () => {
      await loadHistory(token);
      if (!cancelled) {
        setHistoryLoaded(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, historyLoaded, loadHistory]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
    const payload = { conversations, activeConversationId };
    sessionStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(payload));
  }, [conversations, activeConversationId]);

  // Sync theme
  useEffect(() => {
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  // ✅ Login user
  const login = async (email, password) => {
    const response = await loginAPI(email, password);
    localStorage.setItem('authToken', response.token);
    setHistoryLoaded(false);
    setToken(response.token);
    setUser(response.user);
    setIsAuthenticated(true);
  };

  // ✅ Fixed: Pass `user_type` to API
  const register = async (username, email, password, user_type) => {
    const response = await registerAPI(username, email, password, user_type);
    return response;
  };

  // ✅ Logout
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('conversationsState');
    sessionStorage.removeItem(CONVERSATIONS_STORAGE_KEY);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setHistoryLoaded(false);
    const resetConversations = getDefaultConversations();
    const resetId = Object.keys(resetConversations)[0];
    setConversations(resetConversations);
    setActiveConversationId(resetId);
  };

  // Conversation management
  const messages = conversations[activeConversationId]?.messages || [];

  const setMessages = (updater, targetConversationId = activeConversationId) => {
    if (!targetConversationId) return;
    setConversations(prev => {
      const targetConvo = prev[targetConversationId];
      if (!targetConvo) return prev;
      const newMessages =
        typeof updater === 'function' ? updater(targetConvo.messages) : updater;
      return {
        ...prev,
        [targetConversationId]: { ...targetConvo, messages: newMessages },
      };
    });
  };

  const handleNewChat = () => {
    const newId = createConversationId();
    setConversations(prev => ({
      ...prev,
      [newId]: createEmptyConversation(),
    }));
    setActiveConversationId(newId);
  };

  const updateConversationTitle = (title, options = {}) => {
    const { conversationId = activeConversationId, force = false } = options;
    if (!conversationId || !title) return;
    const trimmedTitle = title.trim().substring(0, 80) || 'New Chat';
    setConversations(prev => {
      const targetConvo = prev[conversationId];
      if (!targetConvo) return prev;

      const currentTitle = targetConvo.title || 'New Chat';
      const hasMessages = Array.isArray(targetConvo.messages) && targetConvo.messages.length > 0;
      const shouldKeepCurrent = !force && hasMessages && currentTitle !== 'New Chat';
      if (shouldKeepCurrent || currentTitle === trimmedTitle) {
        return prev;
      }

      return {
        ...prev,
        [conversationId]: { ...targetConvo, title: trimmedTitle },
      };
    });
  };

  const deleteConversation = (conversationId) => {
    const targetConversation = conversations[conversationId];
    if (token && conversationId && targetConversation?.messages?.length) {
      deleteConversationAPI(token, conversationId).catch((err) => {
        console.error('Failed to delete conversation:', err);
      });
    }
    setConversations(prev => {
      if (!prev[conversationId]) return prev;
      const updated = { ...prev };
      delete updated[conversationId];

      let nextActive = activeConversationId;
      if (activeConversationId === conversationId) {
        const remainingIds = Object.keys(updated);
        if (remainingIds.length === 0) {
          const fallbackId = createConversationId();
          updated[fallbackId] = createEmptyConversation();
          nextActive = fallbackId;
        } else {
          nextActive = remainingIds[0];
        }
      }

      if (!updated[nextActive]) {
        const firstAvailable = Object.keys(updated)[0];
        if (firstAvailable) {
          nextActive = firstAvailable;
        } else {
          const fallbackId = createConversationId();
          updated[fallbackId] = createEmptyConversation();
          nextActive = fallbackId;
        }
      }

      setActiveConversationId(nextActive);
      return updated;
    });
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const value = {
    theme,
    toggleTheme,
    isAuthenticated,
    user,
    token,
    appLoading,
    login,
    logout,
    register,
    isAddUserModalOpen,
    openAddUserModal,
    closeAddUserModal,
    isDocumentModalOpen,
    openDocumentModal,
    closeDocumentModal,
    openDocumentModalWith,
    documentPreviewTarget,
    setDocumentPreviewTarget,
    isUploadModalOpen,
    openUploadModal,
    closeUploadModal,
    conversations,
    setConversations,
    activeConversationId,
    setActiveConversationId,
    messages,
    setMessages,
    deleteConversation,
    loading,
    setLoading,
    error,
    setError,
    handleNewChat,
    updateConversationTitle,
    isSidebarOpen,
    setIsSidebarOpen,
    inputDraft,
    setInputDraft,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
